import { pluginRegistry } from "@flow/frontend/member/plugin_api";
import useScript from "@flow/lib/use_script";
import React, { useContext, useEffect } from "react";
import { CookieConsentContext, CookieConsentContextType, CookieConsents } from "./CookieControlContext";

declare const CIVIC_UK_COOKIE_CONTROL_API_KEY: string;
declare const CIVIC_UK_COOKIE_CONTROL_PRODUCT: string;
declare const CIVIC_UK_COOKIE_CONTROL_POLICY_LAST_UPDATED_AT: string;
declare const CIVIC_UK_COOKIE_CONTROL_FACEBOOK_PIXEL_ID: string;

export const analyticsAndMarketingCookieName = "analytics-marketing";
/**
 * Frontend support for configuring the Cookie Control plugin.
 * The facebook pixel code is also injected here, as it is dependent on the cookies being allowed.
 * Pixel ID may be configured in settings.
 */
const cookieControlSrc = "https://cc.cdn.civiccomputing.com/9/cookieControl-9.x.min.js";
const cookieControlConfig = (
  cookieConsentContext: CookieConsentContextType | undefined,
  cookieConsentsRef: React.MutableRefObject<CookieConsents | undefined>
) => ({
  apiKey: CIVIC_UK_COOKIE_CONTROL_API_KEY,
  product: CIVIC_UK_COOKIE_CONTROL_PRODUCT,
  mode: "gdpr",
  logConsent: true,
  subDomains: true,
  initialState: "open",
  position: "left",
  theme: "dark",
  rejectButton: true,
  text: {
    title: "Please be aware that our website uses cookies.",
    intro:
      "Some of these cookies are essential, while others help us to improve your experience by providing insights into how the site is being used.",
    acceptSettings: "Accept All Cookies",
    rejectSettings: "Reject Non-Essential Cookies",
    necessaryTitle: "Essential Cookies",
    necessaryDescription:
      "Essential cookies enable core functionality. The website cannot function properly without these cookies, and these cookies can only be disabled by changing your browser preferences.",
    // thirdPartyTitle : 'Warning: Some cookies require your attention',
    // thirdPartyDescription : 'AddThis will place some cookies on their domain. Consent for these cannot be automatically revoked. Please follow the link below if you want to opt out of them.'
  },
  statement: {
    description: "For an explanation as to how 38 Degrees uses cookies and how this affects you, please",
    name: "click here.",
    url: "https://home.38degrees.org.uk/cookies-policy/",
    updated: CIVIC_UK_COOKIE_CONTROL_POLICY_LAST_UPDATED_AT,
  },
  necessaryCookies: [
    "CookieControl",
    "rack.session",
    "_rack_session",
    "connect.sid",
    "_agra_session",
    "ipp_key",
    "ipp_uid",
    "ipp_uid1",
    "ipp_uid2",
    "_cflb",
    "_cf_bm",
    "_cfduid",
    "_GRECAPTCHA",
    "guid",
  ],
  optionalCookies: [
    {
      name: analyticsAndMarketingCookieName,
      label: "Analytical & Marketing Cookies",
      description:
        "38 Degrees doesn't advertise products. We use analytical & marketing cookies to help us promote campaigns to people we think will be interested in them. If you opt in to these cookies, we'll be able to track our most popular campaigns more accurately, and we'll be able to advertise more relevant campaigns to you on social media.",
      cookies: [
        /*
          Google Analytics & Google Tag Manager cookies:
          see https://developers.google.com/analytics/devguides/collection/analyticsjs/cookie-usage
          Note the '_gcl_au' cookie seems to be undocumented on Google sites, but is a Google Conversion Linker
          cookie, see https://support.google.com/tagmanager/answer/7549390
        */
        "_ga",
        "_gid",
        "_gat",
        "AMP_TOKEN",
        "_gac_*",
        "_gcl_au",
        // Facebook cookies:
        "fr",
        "_fbp",
        "fbq",
        "xs",
        "wd",
        "spin",
        "sb",
        "presence",
        "datr",
        "c_user",
        "usida",
        "oo",
        // Snapchat cookies:
        "X-AB",
        // Posthog cookies
        "sessionid",
        "posthog_csrftoken",
        "ph_current_project_token",
        "ph_current_instance",
        "ph_current_project_name",
        "ph_phc_*_posthog",
      ],
      onAccept: function () {
        // the onAccept callback is also called in the case where page loads and the user has already accepted
        cookieConsentContext?.setCookieConsents({ [analyticsAndMarketingCookieName]: true });
        /* 
          The following is a lot of code that Meta/Facebook recommends using for the Meta pixel. It is
          tremendously ugly, but fixing it to allow our linter to pass it would be both difficult and
          risk breaking what is already a poorly-documented process. More information on the meta pixel
          can be found here: https://www.facebook.com/business/help/952192354843755?id=1205376682832142
        */
        /* eslint-disable prettier/prettier */
        /* eslint-disable prefer-spread */
        /* eslint-disable prefer-rest-params */
          // @ts-ignore
          !function(f,b,e,v,n,t,s) 
          // @ts-ignore
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod? 
          // @ts-ignore
          n.callMethod.apply(n,arguments):n.queue.push(arguments)}; 
          // @ts-ignore
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0'; 
          // @ts-ignore
          n.queue=[];t=b.createElement(e);t.async=!0; 
          // @ts-ignore
          t.src=v;s=b.getElementsByTagName(e)[0]; 
          // @ts-ignore
          s.parentNode.insertBefore(t,s)}(window, document,'script', 'https://connect.facebook.net/en_US/fbevents.js');
          const facebookPixelId = CIVIC_UK_COOKIE_CONTROL_FACEBOOK_PIXEL_ID;
          // @ts-ignore
          fbq('init', facebookPixelId);
          // @ts-ignore
          fbq('track', 'PageView'); 
          const facebookimg = document.createElement('img');
          facebookimg.src = `https://www.facebook.com/tr?id=${facebookPixelId}&ev=PageView&noscript=1`;
          facebookimg.id = "meta-pixel";
          document.body.append(facebookimg);
        /* eslint-enable */
      },
      onRevoke: function () {
        // unfortunately, unlike the onAccept callback, onRevoke only called on user opt-out action, not on page load
        cookieConsentContext?.setCookieConsents({ [analyticsAndMarketingCookieName]: false });
        // @ts-ignore
        if (typeof fbq === "function") {
          // @ts-ignore
          fbq("consent", "revoke");
        }
        document.getElementById("meta-pixel")?.remove();
      },
    },
  ],
  onLoad: function () {
    // onAccept should be called on page load, so this handler is covering case where a user has already revoked consent.
    // (see also comment in onRevoke handler)
    if (cookieConsentsRef.current === undefined) {
      cookieConsentContext?.setCookieConsents({ [analyticsAndMarketingCookieName]: hasAnalyticsCookieConsent() });
    }
  },
});

function hasAnalyticsCookieConsent() {
  const cookieConsentConfig = (window as any).CookieControl.config();
  const analyticsCookiesConfigIndex = cookieConsentConfig.optionalCookies.findIndex(
    (config: { name: string }) => config.name === analyticsAndMarketingCookieName
  );
  if (analyticsCookiesConfigIndex > -1) {
    return (window as any).CookieControl.getCategoryConsent(analyticsCookiesConfigIndex);
  }
  return false;
}

pluginRegistry.registerUtilityComponent({
  def: { provider_unique_id: "cookie_control", label: "Cookie Control" },

  widget: () => {
    const cookieConsentContext = useContext(CookieConsentContext);
    // slightly convoluted but the ref is required here to ensure that the cookieConsents value is up to date when accessed in the onLoad callback.
    const cookieConsentsRef = React.useRef(cookieConsentContext?.cookieConsents);
    cookieConsentsRef.current = cookieConsentContext?.cookieConsents;

    useEffect(() => {
      useScript(cookieControlSrc)
        .then(() => (window as any).CookieControl.load(cookieControlConfig(cookieConsentContext, cookieConsentsRef)))
        // If the script fails to load, we assume that the user has not consented to analytics and marketing cookies.
        // Other CookieConsentContext consumers may have logic that depends on cookieConsents not being undefined.
        .catch(() => cookieConsentContext?.setCookieConsents({ [analyticsAndMarketingCookieName]: false }));
    }, [cookieControlSrc]);
    return <></>;
  },
});
